import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, ResearchDataSubmission, ResearchPermit, ResearchSampleExport } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

/**
 * M34 Blue Economy — foreign/local research permits: live M22 vessel re-verification at apply time,
 * M31 CITES export-permit verification for planned sample exports, and the data-sharing obligation tracker.
 */
export function ResearchPermits() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.includes('BLUE_ECON_OFFICER') : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m34/research-permits?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<ResearchPermit>>(path);

  return (
    <div>
      <PageHeader
        title="Research Permits"
        subtitle="Foreign/local research institutions — live M22 vessel check, M31 CITES export verification, data-sharing tracker (M34)."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New research permit application
          </button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Ref, institution, title…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['APPLIED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'ACTIVE', 'COMPLETED', 'REVOKED'].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>
      {!isOfficer ? <RoleNotice roles={['BLUE_ECON_OFFICER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No research permits match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Institution</th>
                <th className="table-head">Research title</th>
                <th className="table-head">Dates</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{p.refNo}</td>
                  <td className="table-cell text-xs">
                    {p.institutionName}
                    <div className="text-[11px] text-slate-400">{p.institutionType}</div>
                  </td>
                  <td className="table-cell">{p.researchTitle}</td>
                  <td className="table-cell text-xs text-slate-500">
                    {formatDate(p.startDate)} → {formatDate(p.endDate)}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={p.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <PermitDetailModal
          id={selectedId}
          isOfficer={isOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
      {showCreate ? (
        <NewPermitModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function PermitDetailModal({ id, isOfficer, onClose, onChanged }: { id: string; isOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const pq = useApiQuery<ResearchPermit>(`/api/v1/m34/research-permits/${id}`);
  const dataSubsQ = useApiQuery<{ items: ResearchDataSubmission[] }>(`/api/v1/m34/research-permits/${id}/data-submissions`);
  const sampleExportsQ = useApiQuery<{ items: ResearchSampleExport[] }>(`/api/v1/m34/research-permits/${id}/sample-exports`);
  const p = pq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDecision, setShowDecision] = useState<'approve' | 'reject' | 'revoke' | null>(null);
  const [note, setNote] = useState('');
  const [citesSerial, setCitesSerial] = useState('');
  const [showSampleExport, setShowSampleExport] = useState(false);
  const [speciesDescription, setSpeciesDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');

  const refreshAll = () => {
    pq.reload();
    dataSubsQ.reload();
    sampleExportsQ.reload();
    onChanged();
  };
  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      refreshAll();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const startReview = () => call(() => api.post(`/api/v1/m34/research-permits/${id}/start-review`));
  const issue = () => call(() => api.post(`/api/v1/m34/research-permits/${id}/issue`));
  const complete = () => call(() => api.post(`/api/v1/m34/research-permits/${id}/complete`));
  const confirmDecision = () =>
    call(() => {
      if (showDecision === 'approve') return api.post(`/api/v1/m34/research-permits/${id}/decide`, { approve: true, notes: note || undefined });
      if (showDecision === 'reject') return api.post(`/api/v1/m34/research-permits/${id}/decide`, { approve: false, rejectionReason: note });
      return api.post(`/api/v1/m34/research-permits/${id}/revoke`, { reason: note });
    }).then(() => setShowDecision(null));
  const verifyCites = () => call(() => api.post(`/api/v1/m34/research-permits/${id}/cites/verify`, { serial: citesSerial }));
  const recordSampleExport = () =>
    call(() =>
      api.post(`/api/v1/m34/research-permits/${id}/sample-exports`, {
        speciesDescription,
        quantity: Number.parseInt(quantity, 10),
        destinationCountry,
        citesPermitSerial: citesSerial || undefined,
      }),
    ).then(() => {
      setShowSampleExport(false);
      setSpeciesDescription('');
      setQuantity('');
      setDestinationCountry('');
    });

  return (
    <Modal title={p ? `${p.refNo} — ${p.researchTitle}` : 'Research permit'} onClose={onClose} wide>
      {pq.error ? <ErrorNote message={pq.error} /> : null}
      {pq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={p.state} />} />
            <Detail label="Institution" value={`${p.institutionName} (${p.institutionType})`} />
            <Detail label="Principal investigator" value={p.principalInvestigator} />
            <Detail label="Dates" value={`${formatDate(p.startDate)} → ${formatDate(p.endDate)}`} />
            <Detail label="Vessel (M22)" value={p.vesselName ? `${p.vesselName} (${p.vesselRegNo})` : '—'} />
            <Detail label="Fee" value={`${scr(p.feeAmountScr)} — ${p.feeStatus}`} />
            <Detail label="Sample export planned" value={p.sampleExportPlanned ? 'Yes' : 'No'} />
            <Detail label="CITES required" value={p.citesRequired ? 'Yes' : 'No'} />
            <Detail label="CITES permit" value={p.citesPermitSerial ?? '—'} mono />
            <Detail label="Data-sharing due" value={formatDate(p.dataSharingDueAt)} />
            <Detail label="Data-sharing fulfilled" value={formatDateTime(p.dataSharingFulfilledAt)} />
            <Detail label="Permit serial" value={p.permitSerial ?? '—'} mono />
          </div>
          <Detail label="Research summary" value={p.researchSummary} />
          <Detail label="Data-sharing obligation" value={p.dataSharingObligation} />
          {p.rejectionReason ? <ErrorNote message={`Rejected: ${p.rejectionReason}`} /> : null}
          {p.revokedReason ? <ErrorNote message={`Revoked: ${p.revokedReason}`} /> : null}

          {p.sampleExportPlanned ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="field-label">CITES permit verification (M31)</div>
              <div className="flex gap-2">
                <input className="field-input" placeholder="CITES permit serial" value={citesSerial} onChange={(e) => setCitesSerial(e.target.value)} />
                <button className="btn-secondary" disabled={busy || !citesSerial} onClick={verifyCites}>
                  Verify &amp; attach
                </button>
              </div>
            </div>
          ) : null}

          {p.state === 'ACTIVE' ? (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <div className="field-label !mb-0">Recorded sample exports ({sampleExportsQ.data?.items.length ?? 0})</div>
                <button className="text-xs font-semibold text-brand-700 underline" onClick={() => setShowSampleExport((s) => !s)}>
                  + Record export
                </button>
              </div>
              {showSampleExport ? (
                <div className="mb-2 space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label="Species description">
                    <input className="field-input" value={speciesDescription} onChange={(e) => setSpeciesDescription(e.target.value)} />
                  </Field>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Quantity">
                      <input className="field-input" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                    </Field>
                    <Field label="Destination country">
                      <input className="field-input" value={destinationCountry} onChange={(e) => setDestinationCountry(e.target.value)} />
                    </Field>
                  </div>
                  <button className="btn-primary" disabled={busy || !speciesDescription || !quantity || !destinationCountry} onClick={recordSampleExport}>
                    Record
                  </button>
                </div>
              ) : null}
              {(sampleExportsQ.data?.items ?? []).map((e) => (
                <div key={e.id} className="flex justify-between border-b border-slate-100 py-1 text-xs">
                  <span>
                    {e.speciesDescription} × {e.quantity} → {e.destinationCountry}
                  </span>
                  <StatusBadge status={e.citesVerifiedValid ? 'VERIFIED' : 'PENDING'} />
                </div>
              ))}
            </div>
          ) : null}

          {(dataSubsQ.data?.items.length ?? 0) > 0 ? (
            <div>
              <div className="field-label">Data-sharing submissions</div>
              {dataSubsQ.data!.items.map((d) => (
                <div key={d.id} className="flex justify-between border-b border-slate-100 py-1 text-xs">
                  <span>{d.description}</span>
                  <span>{d.reviewedAt ? 'Reviewed' : 'Pending review'}</span>
                </div>
              ))}
            </div>
          ) : null}

          {isOfficer ? (
            <div className="border-t border-slate-100 pt-4">
              {!showDecision ? (
                <div className="flex flex-wrap gap-2">
                  {p.state === 'APPLIED' ? (
                    <button className="btn-primary" disabled={busy || p.feeStatus !== 'PAID'} onClick={startReview}>
                      {p.feeStatus !== 'PAID' ? 'Start review (fee not yet paid)' : 'Start review'}
                    </button>
                  ) : null}
                  {p.state === 'UNDER_REVIEW' ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowDecision('approve')}>
                        Approve
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('reject')}>
                        Reject
                      </button>
                    </>
                  ) : null}
                  {p.state === 'APPROVED' ? (
                    <button className="btn-success" disabled={busy} onClick={issue}>
                      Issue permit (DocVault)
                    </button>
                  ) : null}
                  {p.state === 'ACTIVE' ? (
                    <>
                      <button className="btn-primary" disabled={busy} onClick={complete}>
                        Mark completed
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('revoke')}>
                        Revoke
                      </button>
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label={showDecision === 'approve' ? 'Notes (optional)' : 'Reason'}>
                    <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowDecision(null)}>
                      Cancel
                    </button>
                    <button className={showDecision === 'approve' ? 'btn-success' : 'btn-danger'} disabled={busy} onClick={confirmDecision}>
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewPermitModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [institutionName, setInstitutionName] = useState('');
  const [institutionType, setInstitutionType] = useState<'LOCAL' | 'FOREIGN'>('FOREIGN');
  const [principalInvestigator, setPrincipalInvestigator] = useState('');
  const [researchTitle, setResearchTitle] = useState('');
  const [researchSummary, setResearchSummary] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [vesselRegNo, setVesselRegNo] = useState('');
  const [sampleExportPlanned, setSampleExportPlanned] = useState(false);
  const [dataSharingObligation, setDataSharingObligation] = useState('Raw datasets deposited with SFA within 90 days of research completion.');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m34/research-permits', {
        institutionName,
        institutionType,
        principalInvestigator,
        researchTitle,
        researchSummary,
        startDate,
        endDate,
        vesselRegNo: vesselRegNo || undefined,
        sampleExportPlanned,
        dataSharingObligation,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New research permit application" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Institution name">
            <input className="field-input" value={institutionName} onChange={(e) => setInstitutionName(e.target.value)} />
          </Field>
          <Field label="Institution type">
            <select className="field-input" value={institutionType} onChange={(e) => setInstitutionType(e.target.value as 'LOCAL' | 'FOREIGN')}>
              <option value="FOREIGN">Foreign</option>
              <option value="LOCAL">Local</option>
            </select>
          </Field>
        </div>
        <Field label="Principal investigator">
          <input className="field-input" value={principalInvestigator} onChange={(e) => setPrincipalInvestigator(e.target.value)} />
        </Field>
        <Field label="Research title">
          <input className="field-input" value={researchTitle} onChange={(e) => setResearchTitle(e.target.value)} />
        </Field>
        <Field label="Research summary">
          <textarea className="field-input" rows={2} value={researchSummary} onChange={(e) => setResearchSummary(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date">
            <input className="field-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="End date">
            <input className="field-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>
        <Field label="Vessel registration no. (optional — verified live against M22)">
          <input className="field-input" value={vesselRegNo} onChange={(e) => setVesselRegNo(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={sampleExportPlanned} onChange={(e) => setSampleExportPlanned(e.target.checked)} />
          Sample export planned (requires M31 CITES permit verification before export)
        </label>
        <Field label="Data-sharing obligation">
          <textarea className="field-input" rows={2} value={dataSharingObligation} onChange={(e) => setDataSharingObligation(e.target.value)} />
        </Field>
        <button
          className="btn-primary w-full"
          disabled={busy || !institutionName || !principalInvestigator || !researchTitle || !researchSummary || !startDate || !endDate}
          onClick={submit}
        >
          {busy ? 'Submitting…' : 'Submit application'}
        </button>
      </div>
    </Modal>
  );
}
